import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DoctorAPI, DoctorCreatePayload } from "../../services/doctor.service";
import { SpecializationTagInput } from "../ui/SpecializationTagInput";
import { AddDoctorPrefill } from "../../contexts/AddDoctorContext";
import { isOwnEmail, OWN_EMAIL_MESSAGE } from "../../utils/own-account";

import { DEPARTMENTS } from "../../constants/doctor.constants";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

interface Props { isOpen: boolean; onClose: () => void; onSuccess: () => void; prefill?: AddDoctorPrefill | null; }
interface DaySlot { start_time: string; end_time: string; slot_duration_minutes: number; }
interface ScheduleRow { day_of_week: number; enabled: boolean; slots: DaySlot[]; }

const defaultSchedules = (): ScheduleRow[] =>
  DAYS.map((_, i) => ({ day_of_week: i, enabled: i < 5, slots: [{ start_time: "09:00", end_time: "17:00", slot_duration_minutes: 15 }] }));

export const AddDoctorModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, prefill }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", specializations: [] as string[], department: "",
    registration_number: "", qualifications: "", experience_years: "",
    consultation_fee: "", room_location: "",
  });
  const [schedules, setSchedules] = useState<ScheduleRow[]>(defaultSchedules());
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // When opened from the Add Staff flow (Role = Doctor), pre-fill name + email.
  // Fields stay editable so the admin can still adjust them.
  useEffect(() => {
    if (isOpen && prefill) {
      setForm(f => ({
        ...f,
        name: prefill.name ?? f.name,
        email: prefill.email ?? f.email,
      }));
    }
  }, [isOpen, prefill]);

  if (!isOpen) return null;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Name is required";
    // Email is required: it provisions the doctor's login (Cognito username +
    // invite address) so they can actually sign in and operate.
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (form.specializations.length === 0) e.specializations = "At least one specialization is required";
    if (!form.department) e.department = "Department is required";
    for (const s of schedules) {
      if (!s.enabled) continue;
      for (const slot of s.slots) {
        if (slot.end_time <= slot.start_time) {
          e.schedule = `${DAYS[s.day_of_week]}: end time must be after start time`;
          return e;
        }
      }
      const sorted = [...s.slots].sort((a, b) => a.start_time.localeCompare(b.start_time));
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].start_time < sorted[i - 1].end_time) {
          e.schedule = `${DAYS[s.day_of_week]}: time windows overlap`;
          return e;
        }
      }
    }
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    if (await isOwnEmail(form.email)) { setErrors({ email: OWN_EMAIL_MESSAGE }); return; }
    setLoading(true);
    try {
      const payload: DoctorCreatePayload = {
        name: form.name.trim(),
        specialization:  form.specializations.join(", "),
        specializations: form.specializations,
        department: form.department,
        registration_number: form.registration_number || undefined,
        qualifications: form.qualifications || undefined,
        experience_years: form.experience_years ? parseInt(form.experience_years) : undefined,
        consultation_fee: form.consultation_fee ? parseInt(form.consultation_fee) : undefined,
        room_location: form.room_location || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        availability_schedules: schedules.filter(s => s.enabled).flatMap(s =>
          s.slots.map(slot => ({
            day_of_week: s.day_of_week,
            start_time: `${slot.start_time}:00`,
            end_time: `${slot.end_time}:00`,
            slot_duration_minutes: slot.slot_duration_minutes,
          }))
        ),
      };
      await DoctorAPI.create(payload);
      toast.success("Doctor added successfully");
      onSuccess();
      onClose();
      setForm({ name: "", email: "", phone: "", specializations: [], department: "", registration_number: "", qualifications: "", experience_years: "", consultation_fee: "", room_location: "" });
      setSchedules(defaultSchedules());
      setErrors({});
    } catch (err: any) {
      toast.error(err.message || "Failed to add doctor");
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (dayIdx: number, enabled: boolean) =>
    setSchedules(prev => prev.map((s, i) => i === dayIdx ? { ...s, enabled } : s));

  const updateSlot = (dayIdx: number, slotIdx: number, field: keyof DaySlot, value: any) =>
    setSchedules(prev => prev.map((s, i) => i !== dayIdx ? s : {
      ...s, slots: s.slots.map((sl, j) => j === slotIdx ? { ...sl, [field]: value } : sl),
    }));

  const addSlot = (dayIdx: number) =>
    setSchedules(prev => prev.map((s, i) => i !== dayIdx ? s : (() => {
      const last = s.slots[s.slots.length - 1];
      const [h, m] = last.end_time.split(":").map(Number);
      const endH = Math.min(h + 1, 23);
      const pad = (n: number) => String(n).padStart(2, "0");
      return { ...s, slots: [...s.slots, { start_time: last.end_time, end_time: `${pad(endH)}:${pad(m)}`, slot_duration_minutes: last.slot_duration_minutes }] };
    })()));

  const removeSlot = (dayIdx: number, slotIdx: number) =>
    setSchedules(prev => prev.map((s, i) => i !== dayIdx ? s : {
      ...s, slots: s.slots.filter((_, j) => j !== slotIdx),
    }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full min-w-0 max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Add Doctor</h2>
            <p className="text-sm text-gray-500">Fill in doctor details and schedule</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center text-2xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Full Name — col-span-2 */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
              <input
                className={`w-full border rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.name ? "border-red-400" : "border-gray-200"}`}
                placeholder="Dr. John Doe"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
              <input
                type="email"
                className={`w-full border rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.email ? "border-red-400" : "border-gray-200"}`}
                placeholder="doctor@clinic.com"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="+91 98765 43210"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
              />
            </div>

            {/* Specialization */}
            <div>
              <SpecializationTagInput
                label="Specialization *"
                value={form.specializations}
                onChange={(tags) => setForm({ ...form, specializations: tags })}
                error={errors.specializations}
              />
            </div>

            {/* MCI Registration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">MCI Registration No.</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="MCI-XXXXX"
                value={form.registration_number}
                onChange={e => setForm({ ...form, registration_number: e.target.value })}
              />
            </div>

            {/* Department — col-span-2 so it matches the full width of other col-span-2 fields */}
            <div className="col-span-2">
  <label className="block text-sm font-medium text-gray-700 mb-1">Department <span className="text-red-500">*</span></label>
  <select className={`w-full border rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.department ? "border-red-400":"border-gray-200"}`}
    value={form.department} onChange={e => setForm({...form, department: e.target.value})}>
    <option value="">Select department</option>
    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
  </select>
  {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department}</p>}
</div>

            {/* Qualifications */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Qualifications</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="MBBS, MD..."
                value={form.qualifications}
                onChange={e => setForm({ ...form, qualifications: e.target.value })}
              />
            </div>

            {/* Experience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience (years)</label>
              <input
                type="number"
                min="0"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="10"
                value={form.experience_years}
                onChange={e => setForm({ ...form, experience_years: e.target.value })}
              />
            </div>

            {/* Consultation Fee */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Consultation Fee (₹)</label>
              <input
                type="number"
                min="0"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="500"
                value={form.consultation_fee}
                onChange={e => setForm({ ...form, consultation_fee: e.target.value })}
              />
            </div>

            {/* Room / Location — col-span-2 */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Room / Location</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Room 201, 2nd Floor"
                value={form.room_location}
                onChange={e => setForm({ ...form, room_location: e.target.value })}
              />
            </div>

          </div>

          {/* Availability Schedule */}
          <div>
            <div className="flex items-baseline gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-800">Availability Schedule</h3>
              <span className="text-xs text-gray-400">24-hour format</span>
            </div>
            {errors.schedule && <p className="text-red-500 text-xs mb-2">{errors.schedule}</p>}
            <div className="space-y-2">
              {schedules.map((s, dayIdx) => (
                <div key={dayIdx} className={`rounded-lg ${s.enabled ? "bg-blue-50" : "bg-gray-50"}`}>
                  <div className="flex items-center gap-3 py-1.5 px-2">
                    <input
                      type="checkbox"
                      checked={s.enabled}
                      onChange={e => toggleDay(dayIdx, e.target.checked)}
                      className="rounded w-4 h-4 accent-blue-500 cursor-pointer"
                    />
                    <span className={`text-sm w-24 font-medium ${s.enabled ? "text-gray-800" : "text-gray-400"}`}>
                      {DAYS[dayIdx]}
                    </span>
                    {s.enabled && (
                      <button
                        type="button"
                        onClick={() => addSlot(dayIdx)}
                        className="ml-auto text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                        Window
                      </button>
                    )}
                  </div>
                  {s.slots.map((slot, slotIdx) => (
                    <div key={slotIdx} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-2 pb-2 pl-9">
                      <input
                        type="time"
                        value={slot.start_time}
                        disabled={!s.enabled}
                        onChange={e => updateSlot(dayIdx, slotIdx, "start_time", e.target.value)}
                        className="border border-gray-200 rounded-lg px-3 py-1.5 text-base sm:text-sm bg-white disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-300 w-32"
                      />
                      <span className="text-gray-400 text-sm">to</span>
                      <input
                        type="time"
                        value={slot.end_time}
                        disabled={!s.enabled}
                        onChange={e => updateSlot(dayIdx, slotIdx, "end_time", e.target.value)}
                        className="border border-gray-200 rounded-lg px-3 py-1.5 text-base sm:text-sm bg-white disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-300 w-32"
                      />
                      <select
                        value={slot.slot_duration_minutes}
                        disabled={!s.enabled}
                        onChange={e => updateSlot(dayIdx, slotIdx, "slot_duration_minutes", parseInt(e.target.value))}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-base sm:text-sm bg-white disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-300"
                      >
                        {[10, 15, 20, 30, 45, 60].map(m => <option key={m} value={m}>{m} min</option>)}
                      </select>
                      {s.slots.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSlot(dayIdx, slotIdx)}
                          className="text-gray-300 hover:text-red-400 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                          title="Remove window"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-4 min-h-[44px] text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-5 min-h-[44px] text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-60">
              {loading ? "Saving..." : "Add Doctor"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDoctorModal;